import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { FASAuthService } from 'fas-auth-poc';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  LogLevel,
} from '@microsoft/signalr';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  INTERNAL_EXAM_SESSION_MODE_VALUES,
  InternalExamDeviceVm,
  InternalExamHandoffVm,
  InternalExamLaunchTarget,
  InternalExamLaunchVm,
  InternalExamPairingApprovalVm,
  InternalExamSessionMode,
} from './internal-exam.models';

@Injectable({ providedIn: 'root' })
export class InternalExamService {
  private readonly httpClient = inject(HttpClient);
  private readonly fasAuthService = inject(FASAuthService);
  private presenceConnection?: HubConnection;

  getDevices(): Observable<InternalExamDeviceVm[]> {
    return this.httpClient.get<InternalExamDeviceVm[]>(
      `${environment.webApiUrl}/InternalExamDevices`
    );
  }

  renameDevice(
    deviceGuid: string,
    name: string
  ): Observable<InternalExamDeviceVm> {
    return this.httpClient.put<InternalExamDeviceVm>(
      `${environment.webApiUrl}/InternalExamDevice`,
      { deviceGuid, name }
    );
  }

  /**
   * Removes a station of this OSK: its registration is revoked, a running session on it ends and a
   * connected station signs out. Using the computer again takes a new pairing.
   */
  deleteDevice(deviceGuid: string): Observable<boolean> {
    return this.httpClient.delete<boolean>(
      `${environment.webApiUrl}/InternalExamDevice`,
      { body: { deviceGuid } }
    );
  }

  /**
   * Confirms the code shown on a station that is not signed in: the station becomes a station of
   * this OSK without the OSK password being typed on it.
   */
  approvePairing(pairingCode: string): Observable<InternalExamPairingApprovalVm> {
    return this.httpClient.post<InternalExamPairingApprovalVm>(
      `${environment.webApiUrl}/InternalExamPairingApproval`,
      { pairingCode }
    );
  }

  launchOnDevice(
    target: InternalExamLaunchTarget,
    deviceGuid: string,
    mode: InternalExamSessionMode
  ): Observable<InternalExamLaunchVm> {
    return this.httpClient.post<InternalExamLaunchVm>(
      `${environment.webApiUrl}/InternalExamLaunch`,
      {
        ...this.targetBody(target),
        deviceGuid,
        mode: INTERNAL_EXAM_SESSION_MODE_VALUES[mode],
      }
    );
  }

  endDeviceSession(deviceGuid: string): Observable<boolean> {
    return this.httpClient.delete<boolean>(
      `${environment.webApiUrl}/InternalExamDeviceSession`,
      { body: { deviceGuid } }
    );
  }

  createHandoff(
    target: InternalExamLaunchTarget,
    mode: InternalExamSessionMode
  ): Observable<InternalExamHandoffVm> {
    return this.httpClient.post<InternalExamHandoffVm>(
      `${environment.webApiUrl}/InternalExamHandoff`,
      { ...this.targetBody(target), mode: INTERNAL_EXAM_SESSION_MODE_VALUES[mode] }
    );
  }

  /** The API expects either a student or a lecture group; the absent guid is left out of the body. */
  private targetBody(target: InternalExamLaunchTarget): {
    studentGuid?: string;
    lectureGroupGuid?: string;
  } {
    return target.lectureGroupGuid
      ? { lectureGroupGuid: target.lectureGroupGuid }
      : { studentGuid: target.studentGuid };
  }

  async connectPresence(onDevicesChanged: () => void): Promise<void> {
    if (this.presenceConnection?.state === HubConnectionState.Connected) {
      return;
    }

    this.presenceConnection = new HubConnectionBuilder()
      .withUrl(`${environment.webApiUrl}/hubs/internal-exam-presence`, {
        accessTokenFactory: () => this.fasAuthService.getToken() ?? '',
        transport: HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    this.presenceConnection.on(
      'InternalExamDevicesChanged',
      onDevicesChanged
    );
    await this.presenceConnection.start();
  }

  async disconnectPresence(): Promise<void> {
    if (!this.presenceConnection) {
      return;
    }

    await this.presenceConnection.stop();
    this.presenceConnection = undefined;
  }
}
